import { vec3 } from "gl-matrix";
import { floatToByte, convert, OKLCH, sRGB } from "@texel/color";
import { schemeObservable10 } from "d3-scale-chromatic";
import { TILE_SIZE, type TileMetadata, TileMetadataSchema } from "./schemas.js";
import { compile, constant, swap, dup, sub, Stack } from "./ssl.js";

const DEFAULT_MAX_LIVE_REQUESTS = 6;

export const CANCELLED = Symbol("cancelled");

export interface TileRequest {
  x: number;
  y: number;
  lod: number;
  repo: string;
  kind: string;
  commit: string;
}

interface TileData {
  metadata: TileMetadata;
  data: number[];
}

function compositeTile(
  program: string,
  size: number,
  tiles: number[][],
  buffer: Uint8ClampedArray,
): void {
  const instructions = compile(program);

  const stack = new Stack(10);
  for (let i = 0; i < size; ++i) {
    if (tiles[0]![i]! === 0) {
      continue;
    }

    for (const tile of tiles) {
      stack.push(tile[i]!);
    }

    for (const instruction of instructions) {
      instruction(stack);
    }

    const b = stack.pop();
    const g = stack.pop();
    const r = stack.pop();

    buffer[i * 4 + 0] = r;
    buffer[i * 4 + 1] = g;
    buffer[i * 4 + 2] = b;
    buffer[i * 4 + 3] = 255;

    stack.clear();
  }
}

async function createTileBitmap(
  composite: string,
  tileData: number[],
): Promise<ImageBitmap> {
  const buffer = new Uint8ClampedArray(TILE_SIZE * TILE_SIZE * 4);

  compositeTile(composite, TILE_SIZE * TILE_SIZE, [tileData], buffer);

  const imageData = new ImageData(buffer, TILE_SIZE);

  return await createImageBitmap(imageData, 0, 0, TILE_SIZE, TILE_SIZE, {
    resizeWidth: TILE_SIZE,
    resizeHeight: TILE_SIZE,
    premultiplyAlpha: "none",
    colorSpaceConversion: "none",
    imageOrientation: "none",
    resizeQuality: "pixelated",
  });
}

async function fetchTile(url: string, signal: AbortSignal): Promise<TileData> {
  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const responseText = await response.text();
  const lines = responseText.split("\n");

  if (lines.length < 2) {
    throw new Error(
      "Invalid response format: expected metadata line and data line",
    );
  }

  const rawMetadata = lines[0]!;
  const rawData = lines[1]!;

  let metadata: TileMetadata;
  try {
    metadata = TileMetadataSchema.parse(JSON.parse(rawMetadata));
  } catch (error) {
    throw new Error(`Failed to parse tile metadata: ${error}`);
  }

  // Parse tile data from second line
  let data: number[];
  try {
    data = JSON.parse(rawData);
    if (!Array.isArray(data)) {
      throw new Error("Tile data is not an array");
    }
  } catch (error) {
    throw new Error(`Failed to parse tile data: ${error}`);
  }

  return {
    metadata,
    data,
  };
}

export class TileStore {
  private readonly maxLiveRequests: number;

  private tiles: Map<string, TileData> = new Map();
  private cache: Map<string, WeakRef<TileData>> = new Map();

  private queue: string[] = [];
  private errored: Set<string> = new Set();
  private requested: Set<string> = new Set();
  private live: Set<string> = new Set();
  private aborts: Map<string, () => void> = new Map();
  private preventRequestsTill: number;

  constructor(maxLiveRequests: number = DEFAULT_MAX_LIVE_REQUESTS) {
    this.maxLiveRequests = maxLiveRequests;
    this.preventRequestsTill = performance.now();
  }

  update(urls: string[]): void {
    this.requested.clear();
    this.queue = [];

    // Clear the hard refs:
    this.tiles.clear();

    // Hard ref anything requested that is cached:
    for (const url of urls) {
      this.requested.add(url);

      const tile = this.cache.get(url)?.deref();
      if (tile === undefined) {
        if (!this.live.has(url) && !this.errored.has(url)) {
          this.queue.push(url);
        }
      } else {
        this.tiles.set(url, tile);
      }
    }

    this.queue = this.queue.filter(url => this.requested.has(url));
    for (const [url, abort] of this.aborts.entries()) {
      if (!this.requested.has(url)) {
        abort();
      }
    }

    this.processQueue();
  }

  get(url: string): TileData | undefined {
    return this.tiles.get(url);
  }

  getLiveRequestCount(): number {
    return this.live.size;
  }

  private async requestTile(url: string): Promise<void> {
    this.live.add(url);

    try {
      const controller = new AbortController();
      this.aborts.set(url, () => controller.abort(CANCELLED));
      const signal = controller.signal;
      const tile = await fetchTile(url, signal);
      this.tiles.set(url, tile);
      this.cache.set(url, new WeakRef(tile));
    } catch (error) {
      if (error !== CANCELLED) {
        console.error("Failed to fetch tile:", error);
        this.errored.add(url);
      }
    } finally {
      this.live.delete(url);
      this.aborts.delete(url);
      this.processQueue();
    }
  }

  private processQueue(): void {
    while (this.queue.length > 0 && this.live.size < this.maxLiveRequests) {
      const url = this.queue.shift()!;
      this.requestTile(url);
    }
  }
}

export interface CompositeTileRequest {
  x: number;
  y: number;
  lod: number;
  repo: string;
  commit: string;

  kind: string;
  composite: string;
  aggregation: string;
}

interface CompositorJob {
  // Unique identifier for this request:
  key: string;

  state: "pending" | "processing" | "done";

  // Details of the job:
  x: number;
  y: number;
  lod: number;
  repo: string;
  commit: string;
  kind: string;
  composite: string;

  // Required tiles:
  tiles: string[];

  // Final bitmap:
  bitmap?: ImageBitmap;
}

function compositeToRequiredTiles(r: CompositeTileRequest): string[] {
  const tiles: string[] = [];
  tiles.push(
    `/api/tile/${r.kind}/${r.repo}/${r.commit}/${r.lod}/${r.x}/${r.y}?agg=${r.aggregation}`,
  );
  return tiles;
}

export class TileCompositor {
  private requested: Set<string> = new Set();
  private jobs: Map<string, CompositorJob> = new Map();
  private store: TileStore;

  constructor(store: TileStore) {
    this.store = store;
  }

  private toKey(request: CompositeTileRequest): string {
    return `${request.repo}_${request.commit}_${request.kind}_${request.composite}_${request.x}_${request.y}_${request.lod}`;
  }

  update(requests: CompositeTileRequest[]): void {
    this.requested.clear();

    for (const request of requests) {
      const key = this.toKey(request);
      this.requested.add(key);

      if (!this.jobs.has(key)) {
        this.jobs.set(key, {
          key,
          state: "pending",
          tiles: compositeToRequiredTiles(request),
          ...request,
        });
      }
    }

    const jobsKeys = [...this.jobs.keys()];

    // Remove any dead jobs:
    for (const key of jobsKeys) {
      if (!this.requested.has(key)) {
        this.jobs.delete(key);
      }
    }

    // Request all tiles for active jobs:
    const tiles = [];
    for (const job of this.jobs.values()) {
      for (const tile of job.tiles) {
        tiles.push(tile);
      }
    }
    this.store.update(tiles);

    for (const job of this.jobs.values()) {
      if (job.state !== "pending") {
        continue;
      }

      let ready = true;
      for (const tile of job.tiles) {
        ready = ready && this.store.get(tile) !== undefined;
      }

      if (!ready) {
        continue;
      }

      job.state = "processing";
      this.doJob(job);
    }
  }

  private async doJob(job: CompositorJob): Promise<void> {
    try {
      const data = this.store.get(job.tiles[0]!)!.data;
      const bitmap = await createTileBitmap(job.composite, data);
      job.bitmap = bitmap;
    } catch (error) {
      console.error("Failed to create composite bitmap:", error);
    } finally {
      job.state = "done";
    }
  }

  get(request: CompositeTileRequest): ImageBitmap | undefined {
    const key = this.toKey(request);
    const job = this.jobs.get(key);
    if (job === undefined) {
      return undefined;
    }
    return job.bitmap;
  }

  getProcessingJobCount(): number {
    let count = 0;
    for (const job of this.jobs.values()) {
      if (job.state === "processing") {
        count++;
      }
    }
    return count;
  }
}
